import { useMe } from "@/api/services/main/auth";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useIdle } from "@/hooks/useIdle";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/** Poll `/get-me` every 1 minutes. */
const GET_ME_POLL_INTERVAL = 1 * 60 * 1000;
/** Consider the user idle after 1 minutes without activity. */
const GET_ME_IDLE_TIMEOUT = 1 * 60 * 1000;

/**
 * Keeps the current user (`/get-me`) fresh:
 * - re-fetches every 2 minutes while the user is active,
 * - re-fetches immediately when the user returns from idle, then resumes the cycle,
 *
 * Mount once inside the authenticated shell. Reuses the shared `USER.FETCH_ME`
 * query, so all `useMe()` consumers receive the refreshed data automatically.
 */
export const useMeAutoRefresh = (enabled = true) => {
  const queryClient = useQueryClient();
  const isIdle = useIdle(GET_ME_IDLE_TIMEOUT);
  const wasIdle = useRef(isIdle);

  const query = useMe({
    enabled,
    refetchInterval: enabled && !isIdle ? GET_ME_POLL_INTERVAL : false,
    // Refresh when the user comes back to the browser (window/tab focus).
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (enabled && wasIdle.current && !isIdle) {
      queryClient.refetchQueries({ queryKey: [QUERY_KEYS.USER.FETCH_ME] });
    }
    wasIdle.current = isIdle;
  }, [isIdle, enabled, queryClient]);

  return query;
};
